import {
  AlertCircle,
  CheckCircle2,
  Info,
  TriangleAlert,
  X,
} from "lucide-react";

export type ToastVariant =
  | "success"
  | "error"
  | "warning"
  | "info";

export type ToastMessage = {
  id: string;
  title: string;
  description?: string;
  variant: ToastVariant;
};

type ToastProps = {
  toast: ToastMessage;
  onDismiss: (id: string) => void;
};

const variantStyles: Record<
  ToastVariant,
  {
    container: string;
    iconContainer: string;
    icon: string;
  }
> = {
  success: {
    container: "border-emerald-200 bg-white",
    iconContainer: "bg-emerald-100",
    icon: "text-emerald-600",
  },
  error: {
    container: "border-rose-200 bg-white",
    iconContainer: "bg-rose-100",
    icon: "text-rose-600",
  },
  warning: {
    container: "border-amber-200 bg-white",
    iconContainer: "bg-amber-100",
    icon: "text-amber-600",
  },
  info: {
    container: "border-blue-200 bg-white",
    iconContainer: "bg-blue-100",
    icon: "text-blue-600",
  },
};

function ToastIcon({
  variant,
}: {
  variant: ToastVariant;
}) {
  const className = "h-5 w-5";

  switch (variant) {
    case "success":
      return <CheckCircle2 className={className} />;

    case "error":
      return <AlertCircle className={className} />;

    case "warning":
      return <TriangleAlert className={className} />;

    case "info":
      return <Info className={className} />;
  }
}

export function Toast({
  toast,
  onDismiss,
}: ToastProps) {
  const styles = variantStyles[toast.variant];

  return (
    <div
      role={toast.variant === "error" ? "alert" : "status"}
      className={[
        "pointer-events-auto w-full max-w-sm rounded-2xl border p-4 shadow-xl shadow-slate-950/10",
        "animate-in slide-in-from-right-full fade-in duration-300",
        styles.container,
      ].join(" ")}
    >
      <div className="flex items-start gap-3">
        <div
          className={[
            "flex h-10 w-10 shrink-0 items-center justify-center rounded-xl",
            styles.iconContainer,
            styles.icon,
          ].join(" ")}
        >
          <ToastIcon variant={toast.variant} />
        </div>

        <div className="min-w-0 flex-1 pt-0.5">
          <p className="text-sm font-semibold text-slate-950">
            {toast.title}
          </p>

          {toast.description && (
            <p className="mt-1 text-sm leading-5 text-slate-600">
              {toast.description}
            </p>
          )}
        </div>

        <button
          type="button"
          onClick={() => onDismiss(toast.id)}
          aria-label="Dismiss notification"
          className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-lg text-slate-400 transition hover:bg-slate-100 hover:text-slate-700"
        >
          <X className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
}
